function normalizeRules(rules) {
  if (!rules) return [];
  return Array.isArray(rules) ? rules : [rules];
}

function getQuestionItems(question) {
  return question && Array.isArray(question.questions)
    ? question.questions.filter((item) => item.show !== false)
    : [];
}

function getValueByPath(source, path) {
  if (!source || !path) return undefined;

  return String(path)
    .split(".")
    .reduce(
      (value, key) =>
        value === undefined || value === null ? undefined : value[key],
      source
    );
}

function hasValue(value) {
  return value !== undefined && value !== null && value !== "";
}

function getRuleAnswerValues(question, rule) {
  const answerFields = Array.isArray(rule.answerFields)
    ? rule.answerFields
    : [rule.answerField || "answer"];

  return getQuestionItems(question)
    .filter((item) => item.optional !== true)
    .reduce((values, item) => {
      answerFields.forEach((field) => {
        values.push(getValueByPath(item, field));
      });

      return values;
    }, []);
}

function getComparableValue(value, rule) {
  if (!hasValue(value) && rule.emptyValue !== undefined) {
    return rule.emptyValue;
  }

  return value;
}

function validateAtLeastOneAnswer(question, rule) {
  const values = getRuleAnswerValues(question, rule);

  return values.some(hasValue)
    ? null
    : rule.message || "Please select at least one option before proceeding.";
}

function validateNotAllAnswersEqual(question, rule) {
  const values = getRuleAnswerValues(question, rule);

  if (values.length === 0) {
    return null;
  }

  const disallowedAnswer = rule.answer;
  const comparableValues = values.map((value) =>
    getComparableValue(value, rule)
  );

  if (comparableValues.some((value) => !hasValue(value))) {
    return null;
  }

  const allAnswersMatch = comparableValues.every(
    (value) => value === disallowedAnswer
  );

  return allAnswersMatch
    ? rule.message || `All selections cannot be ${disallowedAnswer}.`
    : null;
}

function shouldRequireDependentField(answer, rule) {
  if (!hasValue(answer)) return false;

  if (Array.isArray(rule.answerValues)) {
    return rule.answerValues.indexOf(answer) !== -1;
  }

  if (Array.isArray(rule.ignoreAnswerValues)) {
    return rule.ignoreAnswerValues.indexOf(answer) === -1;
  }

  return true;
}

function validateRequiredFieldForAnsweredItems(question, rule) {
  const answerField = rule.answerField || "answer";
  const requiredField = rule.requiredField || "subAnswer";
  const hasMissingRequiredField = getQuestionItems(question)
    .filter((item) => item.optional !== true)
    .some((item) => {
      const answer = getValueByPath(item, answerField);
      return (
        shouldRequireDependentField(answer, rule) &&
        !hasValue(getValueByPath(item, requiredField))
      );
    });

  return hasMissingRequiredField
    ? rule.message ||
        "Please complete the required detail for each selected option before proceeding."
    : null;
}

export function validateAnswerValueRules(question) {
  for (const rule of normalizeRules(question && question.answerValueRules)) {
    if (rule.type === "requireAtLeastOneAnswer") {
      const message = validateAtLeastOneAnswer(question, rule);
      if (message) return { message };
    }

    if (rule.type === "notAllAnswersEqual") {
      const message = validateNotAllAnswersEqual(question, rule);
      if (message) return { message };
    }

    if (rule.type === "requireFieldForAnsweredItems") {
      const message = validateRequiredFieldForAnsweredItems(question, rule);
      if (message) return { message };
    }
  }

  return null;
}
